import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Collapse,
  Box,
  Typography,
  Stack,
  useTheme,
  Button,
} from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-mui';
import { RootState } from '../../../reducers';
import { askQuestion } from '../selectedVideoSlice';
import { useLimits } from '../../plan/hooks/limits';

const Suggestions = ({
  chatBoxRef,
  onLimitReached,
}: {
  chatBoxRef: React.RefObject<HTMLDivElement>;
  onLimitReached: () => void;
}) => {
  const {
    file: { _id: fileId },
    chat: { suggestions = [] },
  } = useSelector((state: RootState) => state.selectedVideo);
  const dispatch = useDispatch();
  const [showMore, setShowMore] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [clickCooldown, setClickCooldown] = useState(false);
  const theme = useTheme();
  const guardLimits = useLimits();
  const showLimitReached = guardLimits('askAI') <= 0;

  const handleSuggestionClick = (prompt: string) => {
    if (showLimitReached) {
      onLimitReached();
      return;
    }

    setClickCooldown(true);
    setHoveredIndex(null);
    dispatch(askQuestion({ fileId, question: prompt }));
    setTimeout(() => {
      chatBoxRef.current?.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 300);

    // Reset cooldown after 500ms
    setTimeout(() => {
      setClickCooldown(false);
    }, 500);
  };

  const handleHover = (index: number | null) => {
    if (!clickCooldown) {
      setHoveredIndex(index);
    }
  };

  if (!suggestions.length) return null;

  const suggestionButtonStyle = {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    borderRadius: '20px',
    textTransform: 'none',
    p: 0,
    minHeight: 32,
    display: 'block',
    textAlign: 'left',
    willChange: 'transform, background-color, border-radius',
    transform: 'translateZ(0)',
    transition: 'all 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  };

  const renderSuggestionButton = (
    title: string,
    prompt: string,
    index: number
  ) => {
    const isHovered = hoveredIndex === index;
    return (
      <Button
        key={index}
        onClick={() => handleSuggestionClick(prompt)}
        onMouseEnter={() => handleHover(index)}
        onMouseLeave={() => handleHover(null)}
        variant="text"
        fullWidth
        sx={{
          ...suggestionButtonStyle,
          borderRadius: isHovered ? '20px' : '20px',
        }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isHovered ? 600 : 400,
              transition: 'font-weight 0.15s ease-in-out',
            }}
          >
            {title}
          </Typography>
        </Box>
        <Collapse
          in={isHovered}
          timeout={150}
          sx={{
            transformOrigin: 'top',
            '& .MuiCollapse-wrapperInner': {
              willChange: 'transform, opacity',
              transform: 'translateZ(0)',
            },
          }}
        >
          <Box sx={{ px: 1.5, pb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                lineHeight: 1.4,
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 0.15s ease-in-out',
              }}
            >
              {prompt}
            </Typography>
          </Box>
        </Collapse>
      </Button>
    );
  };

  return (
    <Box sx={{ transform: 'translateZ(0)' }}>
      <Typography
        variant="h5"
        align="center"
        sx={{
          py: 1.5,
          px: 1,
          fontSize: '1.125rem',
          fontWeight: 500,
          color: theme.palette.text.primary,
        }}
      >
        What can I help with?
      </Typography>

      <Stack spacing={1}>
        {suggestions
          .slice(0, 5)
          .map(({ title, prompt }, index) =>
            renderSuggestionButton(title, prompt, index)
          )}

        {suggestions.length > 5 && (
          <>
            <Collapse
              in={showMore}
              timeout={150}
              sx={{
                transformOrigin: 'top',
                '& .MuiCollapse-wrapperInner': {
                  willChange: 'transform, opacity',
                  transform: 'translateZ(0)',
                },
              }}
            >
              <Stack spacing={1}>
                {suggestions
                  .slice(5)
                  .map(({ title, prompt }, index) =>
                    renderSuggestionButton(title, prompt, index + 5)
                  )}
              </Stack>
            </Collapse>
            <Button
              onClick={() => setShowMore((prev) => !prev)}
              fullWidth
              sx={{
                minHeight: 32,
                backgroundColor: 'transparent',
                borderRadius: '20px',
                color: theme.palette.text.primary,
                willChange: 'transform, background-color',
                transform: 'translateZ(0)',
                transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {showMore ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default Suggestions;
